/**
 * ============================================================================
 * Order Extractor V2 - Smart Order Export
 * ============================================================================
 * Version: 1.0.0
 * Created: 2025-12-20
 * 
 * Description:
 *   Smart order export with sync-on-demand. Uses database as primary source
 *   with automatic sync for missing/changed orders. 50-100x faster than v1.
 * 
 * Features:
 *   - Database-first approach (instant for most queries)
 *   - Smart sync: Auto-detects and syncs missing/changed orders
 *   - Order source filtering (website, mkterp, mobile, whatsapp, admin)
 *   - Status filtering (multi-select)
 *   - Excel export with order source column
 *   - Sync progress indicator
 * 
 * ============================================================================
 */

import { useState, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Paper,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    LinearProgress,
    OutlinedInput,
    Grid,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import {
    GetApp as GetAppIcon,
    CloudDownload as CloudDownloadIcon,
    Sync as SyncIcon,
    Inventory as InventoryIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import {
    getOrdersWithSmartSync,
    exportOrdersToExcel,
    exportItemListToPDF,
    exportOrderListToPDF,
    exportPackingListToPDF,
    downloadExcelFile,
    downloadPDFFile,
    markOrdersAsDownloaded,
    OrderExportRequest,
    SyncStats
} from '../../api/orderExtractorV2';
import { orderExtractorV2API } from '../../api';
import { formatDate } from '../../utils/formatters';

const ORDER_STATUSES = [
    'pending',
    'processing',
    'on-hold',
    'completed',
    'cancelled',
    'refunded',
    'failed'
];

const ORDER_SOURCES = [
    { value: 'website', label: 'Website', color: '#2196f3' },
    { value: 'mkterp', label: 'MKTERP', color: '#9c27b0' },
    { value: 'mobile', label: 'Mobile', color: '#ff9800' },
    { value: 'whatsapp', label: 'WhatsApp', color: '#4caf50' },
    { value: 'admin', label: 'Admin', color: '#f44336' },
];

interface OrderRow {
    id: number;
    sNo: number;
    orderNumber: string;
    date: string;
    orderStatus: string;
    customerName: string;
    itemsOrdered: string;
    totalItems: number;
    shippingAddress: string;
    mobileNumber: string;
    orderTotal: number;
    orderSource: string;
    deliveryDate: string;
    deliveryDate: string;
    deliverySlot: string;
    isDownloaded: boolean;
}

export default function OrderExtractorV2() {
    const { enqueueSnackbar } = useSnackbar();

    // State
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState<string[]>([]);
    const [sourceFilter, setSourceFilter] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportingPDF, setExportingPDF] = useState(false);
    const [excelDownloaded, setExcelDownloaded] = useState(false);
    const [orders, setOrders] = useState<OrderRow[]>([]);
    const [selectedOrders, setSelectedOrders] = useState<GridRowSelectionModel>([]);
    const [syncStats, setSyncStats] = useState<SyncStats | null>(null);
    const [processingTime, setProcessingTime] = useState<number | null>(null);


    // Unified export state
    const [generatedFiles, setGeneratedFiles] = useState<string | null>(null);
    const [generating, setGenerating] = useState(false);
    const [filesExpireAt, setFilesExpireAt] = useState<string | null>(null);
    const [generatedOrderIds, setGeneratedOrderIds] = useState<number[]>([]);

    // Compute dispatch slots from orders
    const dispatchSlots = useMemo(() => {
        const slots: Record<string, number[]> = {};

        orders.forEach(order => {
            // Key: "YYYY-MM-DD - SlotName"
            // We want to sort by date, so keeping date at start helps.
            // Display format can be different.
            const date = order.deliveryDate;
            const slot = order.deliverySlot;
            const key = `${date}#${slot}`; // Use # as separator

            if (!slots[key]) {
                slots[key] = [];
            }
            slots[key].push(order.id);
        });

        return Object.entries(slots)
            .sort(([keyA], [keyB]) => {
                const [dateA, slotA] = keyA.split('#');
                const [dateB, slotB] = keyB.split('#');

                // 1. Sort by Date first
                if (dateA !== dateB) {
                    return dateA.localeCompare(dateB);
                }

                // 2. Sort by Time (Chronological)
                // Helper to convert "10:00 AM - 12:00 PM" -> 1000 or "1:00 PM" -> 1300
                const getStartTime = (slot: string) => {
                    const match = slot.match(/(\d+):(\d+)\s*(AM|PM)/i);
                    if (!match) return 9999; // Put invalid/unknown slots at the end

                    let [_, hours, minutes, period] = match;
                    let h = parseInt(hours, 10);
                    const m = parseInt(minutes, 10);

                    if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
                    if (period.toUpperCase() === 'AM' && h === 12) h = 0;

                    return h * 100 + m; // e.g., 1330 for 1:30 PM
                };

                return getStartTime(slotA) - getStartTime(slotB);
            })
            .map(([key, ids]) => {
                const [dateStr, slot] = key.split('#');

                // Format date: YYYY-MM-DD -> DD/MM/YY (Day)
                // Use local time construction to avoid timezone shifts from UTC
                const [y, m, d] = dateStr.split('-').map(Number);
                const dateObj = new Date(y, m - 1, d);

                const day = String(d).padStart(2, '0');
                const month = String(m).padStart(2, '0');
                const year = String(y).slice(-2);
                const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });

                const formattedLabel = `${day}/${month}/${year} (${dayName}) - ${slot}`;

                return {
                    key,
                    label: formattedLabel,
                    ids,
                    count: ids.length
                };
            });
    }, [orders]);

    // Handle slot selection
    const handleSlotChange = (ids: number[], checked: boolean) => {
        const currentSelection = new Set(selectedOrders);
        ids.forEach(id => {
            if (checked) {
                currentSelection.add(id);
            } else {
                currentSelection.delete(id);
            }
        });
        setSelectedOrders(Array.from(currentSelection));
    };

    // Validate dates
    const validateDates = (): boolean => {
        if (!startDate || !endDate) {
            enqueueSnackbar('Please select both start and end dates', { variant: 'warning' });
            return false;
        }

        if (new Date(startDate) > new Date(endDate)) {
            enqueueSnackbar('Start date must be before end date', { variant: 'error' });
            return false;
        }

        return true;
    };

    // Get orders (preview)
    const handleGetOrders = async () => {
        if (!validateDates()) return;

        setLoading(true);
        setOrders([]);
        setSyncStats(null);
        setProcessingTime(null);

        try {
            const request: OrderExportRequest = {
                date_from: startDate,
                date_to: endDate,
                status_filter: statusFilter.length > 0 ? statusFilter : undefined,
                source_filter: sourceFilter.length > 0 ? sourceFilter : undefined,
            };

            const response = await getOrdersWithSmartSync(request);

            // Transform orders for DataGrid
            const transformedOrders: OrderRow[] = response.orders.map((order: any, idx: number) => {
                const itemsOrdered = order.line_items
                    ?.map((item: any) => `${item.name} x ${item.quantity}`)
                    .join(', ') || 'N/A';

                const totalItems = order.line_items?.reduce((sum: number, item: any) => sum + item.quantity, 0) || 0;

                const shipping = order.shipping || {};
                const shippingAddress = [
                    shipping.address_1,
                    shipping.address_2,
                    shipping.city,
                    shipping.state,
                    shipping.postcode
                ].filter(Boolean).join(', ') || 'N/A';

                const billing = order.billing || {};
                const customerName = `${billing.first_name || ''} ${billing.last_name || ''}`.trim() || 'N/A';

                return {
                    id: order.woo_order_id || order.id,
                    sNo: idx + 1,
                    orderNumber: order.order_number || '',
                    date: formatDate(order.date_created),
                    orderStatus: order.status || '',
                    customerName,
                    itemsOrdered,
                    totalItems,
                    shippingAddress,
                    mobileNumber: billing.phone || '',
                    orderTotal: parseFloat(order.total || 0),
                    orderSource: order.order_source || 'website',
                    deliveryDate: order.delivery_date || '-',
                    deliverySlot: order.delivery_slot_id || '-',
                    isDownloaded: !!order.is_downloaded_for_packaging,
                };
            });

            setOrders(transformedOrders);

            // Auto-select only orders with 'processing' or 'pending' status
            const autoSelectedOrders = transformedOrders
                .filter(order => order.orderStatus === 'processing' || order.orderStatus === 'pending')
                .map(order => order.id);

            setSelectedOrders(autoSelectedOrders);
            setSyncStats(response.sync_stats);
            setProcessingTime(response.processing_time_seconds);

            // Show sync stats if any orders were synced
            if (response.sync_stats.synced_count > 0) {
                enqueueSnackbar(
                    `✅ Synced ${response.sync_stats.synced_count} orders ` +
                    `(${response.sync_stats.missing_count} new, ${response.sync_stats.changed_count} updated)`,
                    { variant: 'success' }
                );
            }

            enqueueSnackbar(
                `Found ${response.total_count} orders (${response.processing_time_seconds}s)`,
                { variant: 'success' }
            );
        } catch (error: any) {
            console.error('Error fetching orders:', error);
            enqueueSnackbar(
                error.response?.data?.detail || 'Failed to fetch orders',
                { variant: 'error' }
            );
        } finally {
            setLoading(false);
        }
    };

    // Export to Excel
    const handleExport = async () => {
        if (!validateDates()) return;

        if (selectedOrders.length === 0) {
            enqueueSnackbar('Please select at least one order to export', { variant: 'warning' });
            return;
        }

        setExporting(true);

        try {
            const request: OrderExportRequest = {
                date_from: startDate,
                date_to: endDate,
                status_filter: statusFilter.length > 0 ? statusFilter : undefined,
                source_filter: sourceFilter.length > 0 ? sourceFilter : undefined,
                order_ids: selectedOrders.map(id => Number(id)), // Only export selected orders
            };

            const blob = await exportOrdersToExcel(request);

            // Extract filename from Content-Disposition header (backend provides standardized name)
            // If not available, fallback to standardized format
            const dateParts = endDate.split('-'); // Split YYYY-MM-DD
            const date_str = `${dateParts[2]}-${dateParts[1]}-${dateParts[0].slice(2)}`; // Convert to DD-MM-YY
            const filename = `${date_str}_b2c_order_details.xlsx`;

            // Download file
            downloadExcelFile(blob, filename);

            setExcelDownloaded(true);
            enqueueSnackbar('✅ Orders exported successfully! You can now download the PDF.', { variant: 'success' });
        } catch (error: any) {
            console.error('Error exporting orders:', error);
            enqueueSnackbar(
                error.response?.data?.detail || 'Failed to export orders',
                { variant: 'error' }
            );
        } finally {
            setExporting(false);
        }
    };

    // Export to PDF - Downloads both PDFs
    const handleExportPDF = async () => {
        if (!validateDates()) return;

        setExportingPDF(true);

        try {
            const request: OrderExportRequest = {
                date_from: startDate,
                date_to: endDate,
                status_filter: statusFilter.length > 0 ? statusFilter : undefined,
                source_filter: sourceFilter.length > 0 ? sourceFilter : undefined,
                order_ids: selectedOrders.map(id => Number(id)), // Filter by selected orders
            };

            // Generate date string for filenames
            const fromDate = new Date(startDate);
            const toDate = new Date(endDate);

            const formatDate = (date: Date) => {
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = String(date.getFullYear()).slice(-2);
                return `${day}-${month}-${year}`;
            };

            const dateStr = formatDate(toDate);  // Use only end date

            // Generate standardized filenames: dd-mm-yy_b2c_[type].pdf
            const dateParts = endDate.split('-'); // Split YYYY-MM-DD
            const date_str = `${dateParts[2]}-${dateParts[1]}-${dateParts[0].slice(2)}`; // Convert to DD-MM-YY

            const itemListFilename = `${date_str}_b2c_item_list.pdf`;
            const orderListFilename = `${date_str}_b2c_order_list.pdf`;

            // Download Item List PDF (Portrait)
            const itemListBlob = await exportItemListToPDF(request);
            downloadPDFFile(itemListBlob, itemListFilename);
            enqueueSnackbar('✅ Item List PDF downloaded!', { variant: 'success' });

            // Small delay
            await new Promise(resolve => setTimeout(resolve, 500));

            // Download Order List PDF (Landscape)
            const orderListBlob = await exportOrderListToPDF(request);
            downloadPDFFile(orderListBlob, orderListFilename);
            enqueueSnackbar('✅ Order List PDF downloaded!', { variant: 'success' });

            // Small delay
            await new Promise(resolve => setTimeout(resolve, 500));

            // Download Packing List PDF (Landscape)
            const packingListFilename = `${date_str}_b2c_packing_list.pdf`;
            const packingListBlob = await exportPackingListToPDF(request);
            downloadPDFFile(packingListBlob, packingListFilename);
            enqueueSnackbar('✅ Packing List PDF downloaded!', { variant: 'success' });

            enqueueSnackbar('✅ All PDFs downloaded successfully!', { variant: 'success' });
        } catch (error: any) {
            console.error('Error exporting PDFs:', error);
            enqueueSnackbar(
                error.response?.data?.detail || 'Failed to export PDFs',
                { variant: 'error' }
            );
        } finally {
            setExportingPDF(false);
        }
    };
    // ========================================================================
    // Unified Export Handlers
    // ========================================================================

    const handleGenerateFiles = async () => {
        if (selectedOrders.length === 0) {
            enqueueSnackbar('Please select at least one order', { variant: 'warning' });
            return;
        }

        setGenerating(true);
        try {
            const request = {
                date_from: startDate,
                date_to: endDate,
                order_ids: selectedOrders as number[],
                status_filter: statusFilter.length > 0 ? statusFilter : undefined,
                source_filter: sourceFilter.length > 0 ? sourceFilter : undefined
            };

            const result = await orderExtractorV2API.generateAllFiles(request);

            setGeneratedFiles(result.cache_key);
            setFilesExpireAt(result.expires_at);
            setGeneratedOrderIds(selectedOrders as number[]);
            enqueueSnackbar('✅ All files generated successfully!', { variant: 'success' });
        } catch (error: any) {
            console.error('Error generating files:', error);
            enqueueSnackbar(
                error.response?.data?.detail || 'Failed to generate files',
                { variant: 'error' }
            );
        } finally {
            setGenerating(false);
        }
    };

    const handleDownload = async (fileType: string) => {
        if (!generatedFiles) {
            enqueueSnackbar('Please generate files first', { variant: 'warning' });
            return;
        }

        try {
            // Mark orders as downloaded (fire and forget to not block download)
            if (generatedOrderIds.length > 0) {
                markOrdersAsDownloaded(generatedOrderIds).catch(err =>
                    console.error('Failed to mark orders as downloaded:', err)
                );
            }

            const response = await orderExtractorV2API.downloadFile(generatedFiles, fileType);
            const blob = response.data;

            // Extract filename from header
            const contentDisposition = response.headers['content-disposition'];
            const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
            const filename = filenameMatch?.[1] || `download.${fileType === 'excel' ? 'xlsx' : fileType === 'all' ? 'zip' : 'pdf'}`;

            // Download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            enqueueSnackbar(`✅ ${filename} downloaded!`, { variant: 'success' });
        } catch (error: any) {
            console.error('Error downloading file:', error);
            enqueueSnackbar(
                error.response?.data?.detail || 'Download failed',
                { variant: 'error' }
            );
        }
    };

    // DataGrid columns
    const columns: GridColDef[] = [
        {
            field: 'isDownloaded',
            headerName: 'Pkg',
            width: 50,
            renderCell: (params) => (
                <InventoryIcon
                    color={params.value ? 'success' : 'disabled'}
                    sx={{ opacity: params.value ? 1 : 0.3 }}
                    titleAccess={params.value ? 'Downloaded for Packaging' : 'Not Downloaded'}
                />
            )
        },
        { field: 'sNo', headerName: 'S.No', width: 50 },
        { field: 'orderNumber', headerName: 'Order #', width: 70 },
        { field: 'date', headerName: 'Date', width: 95 },
        { field: 'orderStatus', headerName: 'Status', width: 95 },
        { field: 'orderSource', headerName: 'Source', width: 75 },
        { field: 'deliveryDate', headerName: 'Delivery Date', width: 100 },
        { field: 'deliverySlot', headerName: 'Slot', width: 160 },
        { field: 'customerName', headerName: 'Name', width: 150 },
        { field: 'itemsOrdered', headerName: 'Items Ordered', width: 250 },
        { field: 'totalItems', headerName: 'Total Items', width: 100 },
        { field: 'shippingAddress', headerName: 'Shipping Address', width: 300 },
        { field: 'mobileNumber', headerName: 'Mobile', width: 130 },
        { field: 'orderTotal', headerName: 'Total', width: 100, type: 'number' },
    ];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <CloudDownloadIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box>
                        <Typography variant="h4" fontWeight="bold">
                            Order Extractor V2
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Smart order export with sync-on-demand • 50-100x faster when synced
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Info Alert */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>✨ New Smart Sync:</strong> Automatically checks WooCommerce for missing or changed orders.
                    Uses database for instant results (less than 1s) with automatic sync when needed.
                </Typography>
            </Alert>

            {/* Date Selection */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                    📅 Select Date Range
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                            onClick: (e: any) => {
                                try {
                                    if (e.target.showPicker) e.target.showPicker();
                                } catch (error) {
                                    // Ignore if showPicker is not supported or failed
                                }
                            }
                        }}
                        fullWidth
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                            onClick: (e: any) => {
                                try {
                                    if (e.target.showPicker) e.target.showPicker();
                                } catch (error) {
                                    // Ignore if showPicker is not supported or failed
                                }
                            }
                        }}
                        fullWidth
                    />
                </Box>

                {/* Filters */}
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
                    🔍 Filters (Optional)
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {/* Status Filter */}
                    <FormControl fullWidth>
                        <InputLabel>Order Status</InputLabel>
                        <Select
                            multiple
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as string[])}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {ORDER_STATUSES.map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Source Filter */}
                    <FormControl fullWidth>
                        <InputLabel>Order Source</InputLabel>
                        <Select
                            multiple
                            value={sourceFilter}
                            onChange={(e) => setSourceFilter(e.target.value as string[])}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const source = ORDER_SOURCES.find(s => s.value === value);
                                        return (
                                            <Chip
                                                key={value}
                                                label={source?.label || value}
                                                size="small"
                                                sx={{
                                                    bgcolor: source?.color || '#757575',
                                                    color: 'white'
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {ORDER_SOURCES.map((source) => (
                                <MenuItem key={source.value} value={source.value}>
                                    <Chip
                                        label={source.label}
                                        size="small"
                                        sx={{
                                            bgcolor: source.color,
                                            color: 'white',
                                            mr: 1
                                        }}
                                    />
                                    {source.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={loading ? <CircularProgress size={20} /> : <SyncIcon />}
                        onClick={handleGetOrders}
                        disabled={loading || generating}
                        fullWidth
                    >
                        {loading ? 'Checking for updates...' : 'Get Orders'}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={generating ? <CircularProgress size={20} /> : <GetAppIcon />}
                        onClick={handleGenerateFiles}
                        disabled={loading || generating || selectedOrders.length === 0}
                        fullWidth
                    >
                        {generating ? 'Generating Files...' : `Generate Files (${selectedOrders.length})`}
                    </Button>
                </Box>

                {/* Download Options - Shows after generation */}
                {generatedFiles && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                            💾 Download Options
                            {filesExpireAt && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                    (Files ready for download)
                                </Typography>
                            )}
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleDownload('excel')}
                                    startIcon={<GetAppIcon />}
                                >
                                    📊 Excel
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleDownload('item_pdf')}
                                    startIcon={<GetAppIcon />}
                                >
                                    📄 Item List PDF
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleDownload('order_pdf')}
                                    startIcon={<GetAppIcon />}
                                >
                                    📄 Order List PDF
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleDownload('packing_list_pdf')}
                                    startIcon={<GetAppIcon />}
                                >
                                    📄 Packing List PDF
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleDownload('shipping_labels')}
                                    startIcon={<GetAppIcon />}
                                >
                                    🏷️ Shipping Labels
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => handleDownload('mrp_labels')}
                                    startIcon={<GetAppIcon />}
                                >
                                    🔖 MRP Labels
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    fullWidth
                                    onClick={() => handleDownload('all')}
                                    startIcon={<CloudDownloadIcon />}
                                >
                                    📦 All Files (ZIP)
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Loading Progress */}
                {(loading || exporting) && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {loading && 'Checking WooCommerce for updates...'}
                            {exporting && 'Generating Excel file...'}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Sync Stats Summary */}
            {syncStats && orders.length > 0 && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                        📊 Sync Summary
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
                        {syncStats.synced_count > 0 && (
                            <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                                <Typography variant="h4" fontWeight="bold" color="success.dark">
                                    {syncStats.synced_count}
                                </Typography>
                                <Typography variant="body2" color="success.dark">
                                    Orders Synced
                                </Typography>
                            </Box>
                        )}

                        {syncStats.missing_count > 0 && (
                            <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                                <Typography variant="h4" fontWeight="bold" color="warning.dark">
                                    {syncStats.missing_count}
                                </Typography>
                                <Typography variant="body2" color="warning.dark">
                                    New Orders Added
                                </Typography>
                            </Box>
                        )}

                        {syncStats.changed_count > 0 && (
                            <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                <Typography variant="h4" fontWeight="bold" color="info.dark">
                                    {syncStats.changed_count}
                                </Typography>
                                <Typography variant="body2" color="info.dark">
                                    Orders Updated
                                </Typography>
                            </Box>
                        )}

                        {processingTime !== null && (
                            <Box sx={{ p: 2, bgcolor: 'grey.200', borderRadius: 1 }}>
                                <Typography variant="h4" fontWeight="bold">
                                    {processingTime.toFixed(1)}s
                                </Typography>
                                <Typography variant="body2">
                                    Processing Time
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {syncStats.synced_count === 0 && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            ✅ Database is up-to-date! All orders are already synced.
                        </Alert>
                    )}
                </Paper>
            )}


            {/* Dispatch Slot Selectors */}
            {orders.length > 0 && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom fontWeight="bold">
                        🚚 Bulk Select by Dispatch Slot
                    </Typography>
                    <Grid container spacing={2}>
                        {dispatchSlots.map((slot) => {
                            // Check if all IDs in this slot are selected
                            const allSelected = slot.ids.every(id => selectedOrders.includes(id));
                            // Check if some (but not all) are selected
                            const someSelected = slot.ids.some(id => selectedOrders.includes(id)) && !allSelected;

                            return (
                                <Grid item xs={12} sm={6} md={4} key={slot.key}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={allSelected}
                                                indeterminate={someSelected}
                                                onChange={(e) => handleSlotChange(slot.ids, e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2">
                                                {slot.label} ({slot.count})
                                            </Typography>
                                        }
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>
            )}

            {/* Orders Table */}
            {orders.length > 0 && (
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            📊 Total Orders Found: {orders.length}
                        </Typography>
                    </Box>

                    <Box sx={{ height: 600, width: '100%' }}>
                        <DataGrid
                            rows={orders}
                            columns={columns}
                            checkboxSelection
                            disableRowSelectionOnClick
                            rowSelectionModel={selectedOrders}
                            onRowSelectionModelChange={(newSelection) => setSelectedOrders(newSelection)}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25 } },
                            }}
                            pageSizeOptions={[10, 25, 50, 100]}
                            sx={{
                                border: '1px solid #e0e0e0',
                                '& .MuiDataGrid-cell': {
                                    borderRight: '1px solid #e0e0e0',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    borderBottom: '2px solid #e0e0e0',
                                    backgroundColor: '#fafafa',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    borderRight: '1px solid #e0e0e0',
                                },
                            }}
                        />
                    </Box>
                </Paper>
            )}
        </Box>
    );
}
